import {
  EventRegistrationFormDeregisterMutationVariables,
  EventRegistrationFormFragment,
  EventRegistrationForm_OnlineInfoFragment,
} from './__generated__/Event.graphql';
import {EventState} from './useEventState';
import useSubmitDeregistration from './useSubmitDeregistration';
import useSubmitSelfCheckIn from './useSubmitSelfCheckIn';
import {GroupSessionRequestStatus} from '../../../__generated__/graphql/types';
import {
  DEFAULT_TIME_ZONE,
  THRESHOLD_FOR_ABOUT_TO_START,
} from '../../../constants';
import useAppTheme from '../../../styles/useAppTheme';
import useTypography from '../../../styles/useTypography';
import useForm, {FormState} from '../../hooks/useForm';
import useRecordFieldValidator from '../../hooks/useRecordFieldValidator';
import useFormValidators from '../../hooks/useRecordValidator';
import alwaysDeny from '../../validators/alwaysDeny';
import Button from '../design_system/Button';
import {ButtonColor, ButtonSize} from '../design_system/Button/options';
import Form from '../design_system/form/Form';
import FormTextInput from '../design_system/form/FormTextInput';
import oldUseAppTheme from '../design_system/hooks/useAppTheme';
import TitleText from '../design_system/typography/TitleText';
import FormSubmitButton from '../Form/FormSubmitButton';
import Flex from '../layout/flex/Flex';
import Modal from '../layout/modal/Modal';
import ModalBody from '../layout/modal/ModalBody';

import {css} from '@emotion/react';
import {isAfter, isBefore, subDays} from 'date-fns';
import {formatInTimeZone} from 'date-fns-tz';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

const useSelfCheckInForm = (checkInCode: string, eventID: string) => {
  const {t} = useTranslation();
  const onSubmit = useSubmitSelfCheckIn();

  const checkInCodeValidator = useRecordFieldValidator<
    CheckInFormData,
    'checkInCode'
  >('checkInCode', (input) => {
    if (checkInCode !== input)
      alwaysDeny(
        t(
          'components.event-registration-form.registered-form.modal.invalid-code',
        ),
      )({});
  });

  const onValidate = useFormValidators([checkInCodeValidator]);

  const selfCheckInForm = useForm<CheckInFormData>({
    fallbackFormErrors: [t('form.unknown-form-submission-error')],
    startingValue: {
      eventID,
      checkInCode: '',
    },
    onSubmit,
    onValidate,
    options: {
      forbidSubmissionWhenSuccess: true,
    },
  });

  return {selfCheckInForm};
};

type CheckInFormData = {
  checkInCode: string;
  eventID: string;
};

type SelfCheckInModalProps = {
  eventID: string;
  modalHandler: (displayModal: boolean) => void;
  registeredStatusHandler: (
    registeredStatus: GroupSessionRequestStatus,
  ) => void;
  /**
   * @default ''
   */
  checkInCode?: string;
};

function SelfCheckInModal(props: SelfCheckInModalProps) {
  const {
    checkInCode = '',
    eventID,
    modalHandler,
    registeredStatusHandler,
  } = props;

  const {breakpoints, spacings} = useAppTheme();
  const {title} = useTypography();
  const {t} = useTranslation();
  const [isCompleted, setIsCompleted] = useState(false);
  const {selfCheckInForm} = useSelfCheckInForm(checkInCode, eventID);

  useEffect(() => {
    if (selfCheckInForm.formState === FormState.SUBMITTED_SUCCESS) {
      setIsCompleted(true);
      registeredStatusHandler(GroupSessionRequestStatus.Attended);
      const closeModalTimeout = setTimeout(() => {
        modalHandler(false);
      }, 3000);

      return () => {
        clearTimeout(closeModalTimeout);
      };
    }
  }, [modalHandler, registeredStatusHandler, selfCheckInForm.formState]);

  const modalBodyInnerContainerCss = css({
    display: 'flex',
    flexDirection: 'column',
    gap: spacings.px20,
  });

  const formContainerCss = css({
    display: 'flex',
    background: 'rgba(255, 255, 255, 0.98)',
    inset: '1em',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  });

  const checkInCompletedCss = css([title.s, {[breakpoints.sm]: title.m}]);

  const checkInCompletedTranslation = t(
    'components.event-registration-form.registered-form.modal.check-in-completed',
  );

  const completeCheckInTranslation = t(
    'components.event-registration-form.registered-form.modal.complete-your-check-in',
  );

  const renderCompleteMessage = () => {
    if (!isCompleted) return;
    return (
      <div css={formContainerCss}>
        <span css={checkInCompletedCss}>{checkInCompletedTranslation}</span>
      </div>
    );
  };

  const renderSelfCheckInForm = () => (
    <Form formObject={selfCheckInForm}>
      <FormTextInput
        formObject={selfCheckInForm}
        getValue={(fieldValues) => fieldValues.eventID}
        isReadOnly={true}
        isRequired={true}
        label={t(
          'components.event-registration-form.registered-form.modal.event-id',
        )}
        name="eventID"
        style={css({
          display: 'none',
        })}
      />
      <FormTextInput
        aria-label={t(
          'components.event-registration-form.registered-form.modal.input-label-check-in-code',
        )}
        formObject={selfCheckInForm}
        getValue={(fieldValues) => fieldValues.checkInCode}
        hasRequiredStar={false}
        isRequired={true}
        label={''}
        name="checkInCode"
        placeholder={t(
          'components.event-registration-form.registered-form.modal.check-in-code-placeholder',
        )}
      />
      <FormSubmitButton
        formObject={selfCheckInForm}
        idleLabel={t('common-string-submit')}
        progressLabel={
          isCompleted ? t('common-string-submitted') : t('form.submitting')
        }
      />
    </Form>
  );

  return (
    <ModalBody>
      <div css={modalBodyInnerContainerCss}>
        {renderCompleteMessage()}
        <span css={title.l}>{completeCheckInTranslation}</span>
        {renderSelfCheckInForm()}
      </div>
    </ModalBody>
  );
}

export interface EventExtraDataProps {
  readonly checkInCode?: string;
  readonly surveyUrl?: string;
}

export interface Props {
  readonly event: EventRegistrationFormFragment;
  readonly eventExtraData: EventExtraDataProps;
  readonly eventID: string;
  readonly eventState: EventState;
  readonly onlineInfo: Maybe<EventRegistrationForm_OnlineInfoFragment>;
  readonly registeredStatus: GroupSessionRequestStatus;
  readonly isDisabled?: boolean;
}

export default function RegistrationFormRegistered(props: Props): JSX.Element {
  const {
    event,
    eventExtraData: eventData,
    eventID,
    eventState,
    isDisabled,
    onlineInfo,
    registeredStatus,
  } = props;
  const {spacing} = oldUseAppTheme();
  const {t} = useTranslation();
  const [displayModal, setDisplayModal] = useState(false);
  const [currentRegisteredStatus, setCurrentRegisteredStatus] =
    useState<GroupSessionRequestStatus>(registeredStatus);

  const doMutation = useSubmitDeregistration();
  const formObj = useForm<EventRegistrationFormDeregisterMutationVariables>({
    fallbackFormErrors: [t('form.unknown-form-submission-error')],
    startingValue: {
      eventID,
    },
    onSubmit: doMutation,
  });

  const handleRegisteredStatus = (
    registeredStatus: GroupSessionRequestStatus,
  ) => {
    setCurrentRegisteredStatus(registeredStatus);
  };

  const handleModal = (displayModal: boolean) => {
    setDisplayModal(displayModal);
  };

  const renderTitleText = () => {
    if (
      currentRegisteredStatus === GroupSessionRequestStatus.Attended &&
      eventState !== 'completed'
    ) {
      return eventData.surveyUrl
        ? t(
            'components.event-registration-form.registered-form.complete-survey',
          )
        : t(
            'components.event-registration-form.registered-form.thank-you-attending',
          );
    }

    return eventState === 'about-to-start' || eventState === 'in-progress'
      ? t(
          'components.event-registration-form.registered-form.complete-your-check-in',
        )
      : eventState === 'completed'
        ? t('components.event-registration-form.registered-form.event-ended')
        : t('components.event-registration-form.already-registered-message');
  };

  const renderJoinButton = () => {
    if (onlineInfo == null) return;

    const now = new Date();

    const joinableTimeDate = new Date(onlineInfo.joinableTime);
    const isJoinable =
      !isDisabled &&
      !isBefore(now, joinableTimeDate) &&
      isAfter(now, joinableTimeDate);

    if (isJoinable) {
      return (
        <Button
          color={ButtonColor.ACCENT}
          label={t('components.event-registration-form.join-meeting-cta-label')}
          size={ButtonSize.LARGE}
          sx={{width: '100%'}}
          to={onlineInfo.url}
        />
      );
    }

    const joinButtonLabel = t(
      'components.event-registration-form.inactive-join-meeting-label',
      {
        joinableTime: formatInTimeZone(
          joinableTimeDate,
          DEFAULT_TIME_ZONE,
          'h:mm a',
        ),
      },
    );

    return (
      <Button
        isDisabled={true}
        label={joinButtonLabel}
        size={ButtonSize.LARGE}
        sx={{width: '100%'}}
      />
    );
  };

  const isCheckInButtonDisabled =
    isDisabled ||
    isBefore(
      new Date(),
      subDays(new Date(event.startTimeUTCSeconds * 1000), 1),
    );

  const checkInButtonLabel =
    eventState === 'pending'
      ? t('components.event-registration-form.registered-form.check-in-time', {
          time: formatInTimeZone(
            new Date(
              (event.startTimeUTCSeconds - THRESHOLD_FOR_ABOUT_TO_START) * 1000,
            ),
            DEFAULT_TIME_ZONE,
            'h:mm a',
          ),
        })
      : t('components.event-registration-form.registered-form.self-check-in');

  return (
    <>
      <Modal
        shown={displayModal}
        onClose={() => handleModal(false)}
      >
        <SelfCheckInModal
          checkInCode={eventData.checkInCode}
          eventID={eventID}
          modalHandler={handleModal}
          registeredStatusHandler={handleRegisteredStatus}
        />
      </Modal>
      <Flex
        direction="column"
        sx={css({
          width: '100%',
          rowGap: spacing.x32,
          textAlign: 'center',
        })}
      >
        <TitleText
          size="XS"
          sx={css({
            textAlign: 'center',
          })}
        >
          {renderTitleText()}
        </TitleText>
        {currentRegisteredStatus === GroupSessionRequestStatus.Attended ? (
          eventData.surveyUrl && (
            <Button
              color={ButtonColor.ACCENT}
              label={t(
                'components.event-registration-form.registered-form.complete-survey',
              )}
              size={ButtonSize.LARGE}
              sx={css({
                width: '100%',
              })}
              to={eventData.surveyUrl}
            />
          )
        ) : (
          <Button
            color={ButtonColor.ACCENT}
            isDisabled={isCheckInButtonDisabled}
            label={checkInButtonLabel}
            size={ButtonSize.LARGE}
            sx={css({
              width: '100%',
            })}
            onPress={() => handleModal(true)}
          />
        )}

        {renderJoinButton()}

        {currentRegisteredStatus !== GroupSessionRequestStatus.Attended && (
          <Form formObject={formObj}>
            <FormSubmitButton
              color={ButtonColor.SECONDARY}
              formObject={formObj}
              idleLabel={t(
                'components.event-registration-form.deregister-cta-label',
              )}
              isDisabled={isDisabled}
              progressLabel={t('form.submitting')}
              sx={css({
                width: 'auto',
              })}
            />
          </Form>
        )}
      </Flex>
    </>
  );
}
